// SOC 2 Evidence-Engine tab body — extracted from the original
// AdminComplianceSOC2 page so it can render inside the consolidated
// `/admin/compliance` shell alongside the ISO 27001 tab.

import { useEffect, useMemo, useState } from 'react';
import { Download, Eye, History, RefreshCw } from 'lucide-react';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import {
  useSOC2Criteria,
  useSOC2Runs,
  previewSOC2,
  generateSOC2Bundle,
  type SOC2Report,
  type GenerateResult,
} from '../../api/complianceSoc2';

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MiB`;
}

function shortHash(s: string | undefined): string {
  if (!s) return '—';
  if (s.length <= 12) return s;
  return s.slice(0, 12) + '…';
}

export function AdminComplianceSoc2Tab() {
  const { data: catalogue } = useSOC2Criteria();
  const { data: runsData, mutate: refreshRuns } = useSOC2Runs();

  const today = useMemo(() => new Date(), []);
  const ninetyDaysAgo = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() - 90);
    return d;
  }, [today]);

  const [start, setStart] = useState(isoDate(ninetyDaysAgo));
  const [end, setEnd] = useState(isoDate(today));
  const [note, setNote] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState<SOC2Report | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastDownload, setLastDownload] = useState<GenerateResult | null>(null);

  // Once the catalogue loads, default-select every criterion. The
  // user can deselect to narrow the report.
  useEffect(() => {
    if (catalogue && selected.size === 0) {
      setSelected(new Set(catalogue.criteria.map((c) => c.id)));
    }
  }, [catalogue]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleCriterion = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelected(next);
  };

  const handlePreview = async () => {
    setError(null);
    setPreview(null);
    setPreviewing(true);
    try {
      const report = await previewSOC2({
        start,
        end,
        criteria: Array.from(selected),
      });
      setPreview(report);
    } catch (e) {
      setError(String(e));
    } finally {
      setPreviewing(false);
    }
  };

  const handleGenerate = async () => {
    if (
      !globalThis.confirm(
        'Generate the SOC 2 bundle now? This will write an audit row recording the scope, criteria, and bundle SHA-256. The ZIP file downloads to your browser; OrbitalReg does NOT retain the bundle bytes.',
      )
    ) {
      return;
    }
    setError(null);
    setLastDownload(null);
    setGenerating(true);
    try {
      const out = await generateSOC2Bundle({
        start,
        end,
        criteria: Array.from(selected),
        note: note.trim(),
      });
      setLastDownload(out);
      await refreshRuns();
    } catch (e) {
      setError(String(e));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader
          title="Generate report"
          description="Pick a scope window and the criteria to include. Preview is read-only; Generate writes the audit row and downloads the ZIP."
        />
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="soc2-scope-start" className="block text-sm text-fg-muted mb-1">Scope start</label>
              <Input
                id="soc2-scope-start"
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="soc2-scope-end" className="block text-sm text-fg-muted mb-1">Scope end</label>
              <Input
                id="soc2-scope-end"
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
              />
            </div>
          </div>

          <div className="mt-5">
            <p className="text-sm text-fg-muted mb-2">Trust-Service-Criteria</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {catalogue?.criteria.map((c) => (
                <label
                  key={c.id}
                  className="flex items-start gap-3 p-3 rounded-md border border-border hover:bg-bg-hover/40 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(c.id)}
                    onChange={() => toggleCriterion(c.id)}
                    className="mt-1"
                  />
                  <span>
                    <span className="font-medium text-fg">{c.title}</span>
                    <span className="block text-sm text-fg-muted mt-0.5">
                      {c.description}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="mt-5">
            <label htmlFor="soc2-note" className="block text-sm text-fg-muted mb-1">
              Note (optional — recorded with the run, surfaced in the past-runs list)
            </label>
            <Input
              id="soc2-note"
              placeholder="e.g. Q3 2025 fieldwork — Acme Auditor LLP"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={400}
            />
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="md"
              onClick={handlePreview}
              disabled={previewing || selected.size === 0}
            >
              <Eye className="h-4 w-4 mr-1.5" />
              {previewing ? 'Building preview…' : 'Preview'}
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={handleGenerate}
              disabled={generating || selected.size === 0}
            >
              <Download className="h-4 w-4 mr-1.5" />
              {generating ? 'Generating…' : 'Generate & download'}
            </Button>
            {selected.size === 0 && (
              <span className="text-sm text-warning">
                Pick at least one criterion.
              </span>
            )}
          </div>

          {error && (
            <p className="mt-4 text-sm text-danger font-mono">{error}</p>
          )}

          {lastDownload && (
            <div className="mt-4 p-3 rounded-md bg-success/10 border border-success/30 text-sm">
              <p className="text-success font-medium">
                Downloaded {lastDownload.filename}
              </p>
              <p className="text-fg-muted mt-1">
                {lastDownload.itemCount} evidence items · {formatBytes(lastDownload.size)} ·
                SHA-256 <code className="text-xs">{lastDownload.sha256}</code>
              </p>
            </div>
          )}
        </CardBody>
      </Card>

      {preview && (
        <Card className="mt-6">
          <CardHeader
            title="Preview"
            description={`Generated ${new Date(preview.generated_at).toUTCString()} · ${preview.sections.reduce(
              (n, s) => n + s.items,
              0,
            )} total evidence items.`}
          />
          <CardBody>
            <div className="space-y-4">
              {preview.sections.map((s) => (
                <div
                  key={s.criterion}
                  className="rounded-md border border-border p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-medium text-fg">{s.title}</h3>
                    <Badge tone="accent">{s.items} item(s)</Badge>
                  </div>
                  <p className="text-sm text-fg-muted mt-2">{s.narrative}</p>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      <Card className="mt-6">
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              <History className="h-4 w-4 text-fg-muted" />
              Past runs
            </span>
          }
          description="Every previous bundle generation with its hash. Bundle bytes are not retained server-side."
          actions={
            <Button variant="ghost" size="sm" onClick={() => refreshRuns()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          }
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 font-medium text-fg-muted">Generated</th>
                <th className="px-5 py-3 font-medium text-fg-muted">By</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Scope</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Criteria</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Items</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Size</th>
                <th className="px-5 py-3 font-medium text-fg-muted">SHA-256</th>
              </tr>
            </thead>
            <tbody>
              {runsData?.runs?.length ? (
                runsData.runs.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-border/60 last:border-0"
                  >
                    <td className="px-5 py-3 whitespace-nowrap text-fg">
                      {new Date(r.generated_at).toLocaleString()}
                    </td>
                    <td className="px-5 py-3 text-fg-muted">
                      {r.generated_by || '—'}
                    </td>
                    <td className="px-5 py-3 text-fg-muted whitespace-nowrap">
                      {new Date(r.scope_start).toISOString().slice(0, 10)}
                      {' → '}
                      {new Date(r.scope_end).toISOString().slice(0, 10)}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap gap-1">
                        {r.criteria.map((c) => (
                          <Badge key={c} tone="neutral">
                            {c}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-fg">{r.item_count}</td>
                    <td className="px-5 py-3 text-fg-muted">
                      {formatBytes(r.bundle_size)}
                    </td>
                    <td
                      className="px-5 py-3 font-mono text-xs text-fg-muted"
                      title={r.bundle_sha256}
                    >
                      {shortHash(r.bundle_sha256)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={7}
                    className="px-5 py-8 text-center text-fg-muted text-sm"
                  >
                    No SOC 2 evidence bundles generated yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}
